import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const s = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 200,
    background: '#ffffff',
    borderBottom: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    height: '68px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  logoImg: {
    height: '44px',
    width: 'auto',
    display: 'block',
    objectFit: 'contain',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  adminBtn: {
    padding: '10px 22px',
    background: 'var(--teal-dark)',
    color: '#ffffff',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    fontWeight: 700,
    transition: 'all 0.18s',
    letterSpacing: '0.01em',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(13, 79, 82, 0.18)',
  },
  supportBtn: {
    padding: '10px 22px',
    background: 'var(--teal)',
    color: '#ffffff',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    fontWeight: 700,
    transition: 'all 0.18s',
    letterSpacing: '0.01em',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(29, 156, 156, 0.20)',
  },
  logoutBtn: {
    padding: '9px 18px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid var(--border)',
    transition: 'all 0.15s',
  },
  adminBadge: {
    padding: '4px 12px',
    background: 'var(--teal-light)',
    color: 'var(--teal-dark)',
    borderRadius: 'var(--radius-pill)',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid rgba(29,156,156,0.3)',
  },
};

export default function Header({ onAdminClick }) {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide "Submit a Support Request" on admin pages (login + admin panel).
  const isAdminRoute = location.pathname === '/admin' || location.pathname === '/dash-control';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header style={s.header}>
      <div style={s.inner}>
        {/* Logo */}
        <a href="https://ai.deliverhealth.com/" target="_blank" rel="noopener noreferrer" style={s.logo}>
          <img
            src="/deliverhealth-logo.jpg"
            alt="DeliverHealth"
            style={s.logoImg}
            onError={(e) => {
              // Fallback inline SVG mark + wordmark — matches the DeliverHealth brand layout:
              // small orange top-left, large navy top-right, small yellow bottom-left, large teal bottom-right
              const fallback = document.createElement('span');
              fallback.style.display = 'inline-flex';
              fallback.style.alignItems = 'center';
              fallback.style.gap = '12px';
              fallback.innerHTML = `
                <svg width="50" height="50" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
                  <!-- Bottom-right: large teal -->
                  <circle cx="34" cy="34" r="17" fill="#2EAEA1"/>
                  <!-- Top: large dark navy -->
                  <circle cx="25" cy="18" r="14" fill="#1F3A56"/>
                  <!-- Top-left: small orange/coral -->
                  <circle cx="11" cy="13" r="9" fill="#EE6A45"/>
                  <!-- Bottom-left: small yellow -->
                  <circle cx="11" cy="30" r="8" fill="#F4C23C"/>
                </svg>
                <span style="font-size:26px;font-weight:800;color:#1F3A56;letter-spacing:-0.6px;font-family:system-ui,-apple-system,'Segoe UI',sans-serif">
                  DeliverHealth
                </span>
              `;
              e.currentTarget.replaceWith(fallback);
            }}
          />
        </a>

        {/* Right side */}
        <div style={s.right}>
          {!isAdminRoute && (
            <a
              href="https://communityportal.deliverhealth.com/Support/"
              target="_blank"
              rel="noopener noreferrer"
              style={s.supportBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 18px rgba(29, 156, 156, 0.45), 0 1px 2px rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(29, 156, 156, 0.28), 0 1px 2px rgba(0,0,0,0.05)';
              }}
            >
              Submit a Support Request
            </a>
          )}

          {/* Admin Panel button — only visible when already logged in as admin */}
          {isAdmin && (
            <button
              style={s.adminBtn}
              onClick={onAdminClick}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 18px rgba(13, 79, 82, 0.45), 0 1px 2px rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 79, 82, 0.28), 0 1px 2px rgba(0,0,0,0.05)';
              }}
            >
              ⚙️ Admin Panel
            </button>
          )}

          {user && (
            <button
              style={s.logoutBtn}
              onClick={handleLogout}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--teal)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              Sign Out
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
